// AI 기반 YouTube 추천 서비스 (YouTube API 포함)
class YouTubeService {
  constructor() {
    this.aiService = null;
    this.cache = new Map(); // 간단한 캐시
    this.cacheTimeout = 5 * 60 * 1000; // 5분
    this.apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;
  }

  setAIService(aiService) {
    this.aiService = aiService;
  }

  isConfigured() {
    return !!this.apiKey;
  }

  handleError(error) {
    console.error('AI YouTube Service Error:', error);
    return { success: false, error: error.message || 'AI 추천 서비스 오류' };
  }

  // ---------- 내부 유틸 ----------
  extractFirstJSONObject(text) {
    if (typeof text !== 'string') return null;
    const m = text.match(/\{[\s\S]*\}$/);
    if (!m) return null;
    try { return JSON.parse(m[0]); } catch { return null; }
  }

  toInt(val, fallback = 0) {
    const n = Number(val);
    return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : fallback;
  }

  trimStr(val) {
    return typeof val === 'string' ? val.trim() : '';
  }

  // 캐시 키 생성
  generateCacheKey(query, maxResults, language, skipCache = false) {
    if (skipCache) {
      // 새로운 추천을 위해 랜덤 값 추가
      return `${query}_${maxResults}_${language}_${Date.now()}_${Math.random()}`;
    }
    return `${query}_${maxResults}_${language}`;
  }

  // 캐시에서 가져오기
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    this.cache.delete(key);
    return null;
  }

  // 캐시에 저장
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  // YouTube Data API로 플레이리스트 제목 가져오기
  async getPlaylistInfo(playlistId) {
    if (!this.isConfigured()) {
      return null;
    }

    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/playlists?part=snippet&id=${playlistId}&key=${this.apiKey}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          return {
            title: data.items[0].snippet.title,
            description: data.items[0].snippet.description,
            thumbnail: data.items[0].snippet.thumbnails?.default?.url
          };
        }
      }
    } catch (error) {
      console.error('YouTube API Error:', error);
    }
    return null;
  }

  // 플레이리스트의 곡들을 가져오기
  async getPlaylistItems(playlistId, maxResults = 10) {
    if (!this.isConfigured()) {
      return null;
    }

    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&playlistId=${playlistId}&maxResults=${maxResults}&key=${this.apiKey}`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          return data.items.map(item => ({
            title: item.snippet.title,
            description: item.snippet.description,
            channelTitle: item.snippet.channelTitle,
            publishedAt: item.snippet.publishedAt
          }));
        }
      }
    } catch (error) {
      console.error('YouTube API Error:', error);
    }
    return null;
  }

  // 플레이리스트 제목에서 키워드 추출
  extractKeywordsFromTitle(title) {
    if (!title) return [];
    const keywords = [];
    
    // 일반적인 음악 관련 키워드
    const musicKeywords = ['음악', 'music', '플레이리스트', 'playlist', '노래', 'song', '곡', 'track'];
    const genreKeywords = ['팝', 'pop', '재즈', 'jazz', '클래식', 'classical', '락', 'rock', '힙합', 'hiphop', '일렉트로닉', 'electronic', 'R&B', 'rnb', '컨트리', 'country', '블루스', 'blues', '펑크', 'punk', '메탈', 'metal', '소울', 'soul', '레게', 'reggae', '포크', 'folk', '인디', 'indie', '알앤비', 'rnb'];
    const moodKeywords = ['차분한', 'chill', '신나는', 'energetic', '우울한', 'sad', '행복한', 'happy', '로맨틱', 'romantic', '스트레스', 'stress', '운동', 'workout', '공부', 'study', '집중', 'focus', '휴식', 'relax', '파티', 'party', '드라이브', 'drive', '잠잘때', 'sleep'];
    
    const lowerTitle = title.toLowerCase();
    
    // 키워드 매칭
    [...musicKeywords, ...genreKeywords, ...moodKeywords].forEach(keyword => {
      if (lowerTitle.includes(keyword.toLowerCase())) {
        keywords.push(keyword);
      }
    });
    
    return [...new Set(keywords)]; // 중복 제거
  }

  // 모델이 다르게 준 키/형식을 우리가 원하는 스키마로 강제 보정
  coercePlaylists(parsed, maxResults) {
    // 흔한 변형: results/items/playlists 등
    let arr =
      parsed?.playlists ||
      parsed?.results ||
      parsed?.items ||
      parsed?.data ||
      [];

    if (!Array.isArray(arr)) arr = [];

    const out = [];
    for (const it of arr) {
      if (!it || typeof it !== 'object') continue;

      const id = this.trimStr(it.id || it.playlistId || it.pid || '');
      const title = this.trimStr(it.title || it.name || it.playlist || '');
      const description = this.trimStr(it.description || it.desc || '');
      const channelTitle = this.trimStr(it.channelTitle || it.channel || it.owner || '');
      const videoCount = this.toInt(it.videoCount ?? it.count ?? it.size ?? 0, 0);

      // 최소 요건: title은 있어야 유저가 볼 수 있음
      if (!title) continue;

      // 키워드 추출
      const keywords = this.extractKeywordsFromTitle(title);

      out.push({
        id: id || undefined, // 없으면 undefined로
        title,
        description,
        channelTitle,
        thumbnail: this.getRandomMusicThumbnail(title, keywords), // 항상 우리가 생성한 이미지 사용
        videoCount,
        keywords
      });
    }

    // 빈 값/중복 제거(제목 기준)
    const seen = new Set();
    const deduped = [];
    for (const p of out) {
      const key = `${p.title.toLowerCase()}|${p.channelTitle?.toLowerCase() || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      deduped.push(p);
      if (deduped.length >= maxResults) break;
    }

    return deduped;
  }

  // 다양한 음악 관련 썸네일 생성
  getRandomMusicThumbnail(title, keywords = []) {
    // 무작위 번호 생성 (1-1000)
    const randomSeed = Math.floor(Math.random() * 1000) + 1;
    
    // Picsum Photos를 사용하여 진짜 랜덤 이미지 생성
    const imageId = (randomSeed % 50) + 1;  // 1-50 사이의 ID
    const randomParam = Date.now() + Math.random();
    
    return `https://picsum.photos/320/180?random=${randomParam}&sig=${imageId}`;
  }

  // 스마트 쿼리 생성
  generateSmartQuery(context, language = 'ko') {
    const isKorean = language === 'ko';
    
    if (context.type === 'currentSong') {
      const songTitle = context.songTitle || '';
      // 곡 제목에서 장르/분위기 키워드 추출
      const keywords = this.extractKeywordsFromTitle(songTitle);
      if (keywords.length > 0) {
        const keyword = keywords[0];
        return isKorean ? `${keyword} 음악 플레이리스트` : `${keyword} music playlist`;
      }
      return isKorean ? `${songTitle} 유사 음악` : `${songTitle} similar music`;
    }
    
    if (context.type === 'history') {
      const history = context.history || [];
      if (history.length === 0) {
        return isKorean ? '인기 음악 플레이리스트' : 'Popular Music Playlist';
      }
      
      // 가장 많이 재생된 플레이리스트 분석
      const mostPlayed = history.reduce((max, p) => 
        (p.playCount || 0) > (max.playCount || 0) ? p : max
      );
      
      const keywords = this.extractKeywordsFromTitle(mostPlayed.title);
      if (keywords.length > 0) {
        const keyword = keywords[0];
        return isKorean ? `${keyword} 음악 플레이리스트` : `${keyword} music playlist`;
      }
      
      return isKorean ? `${mostPlayed.title} 플레이리스트` : `${mostPlayed.title} playlist`;
    }
    
    if (context.type === 'genre') {
      return isKorean ? `${context.genre} 음악 플레이리스트` : `${context.genre} music playlist`;
    }
    
    if (context.type === 'mood') {
      return isKorean ? `${context.mood} 음악 플레이리스트` : `${context.mood} music playlist`;
    }
    
    return isKorean ? '인기 음악 플레이리스트' : 'Popular Music Playlist';
  }

  // ---------- 메인 ----------
  async generateAIPlaylistRecommendations(context, maxResults = 5, language = 'ko', skipCache = false) {
    if (!this.aiService) {
      return { success: false, error: 'AI 서비스가 초기화되지 않았습니다.' };
    }

    try {
      const isKorean = language === 'ko';
      const query = this.generateSmartQuery(context, language);
      
      // 캐시 완전 비활성화 (항상 새로운 결과 생성)
      // if (!skipCache) {
      //   const cacheKey = this.generateCacheKey(query, maxResults, language);
      //   const cached = this.getFromCache(cacheKey);
      //   if (cached) {
      //     return { success: true, playlists: cached, fromCache: true };
      //   }
      // }

      const prompt = `
${isKorean ? '다음 컨텍스트에 맞는 YouTube 플레이리스트를 추천하세요:' : 'Recommend YouTube playlists for the context:'}

${isKorean ? '컨텍스트:' : 'Context:'} ${JSON.stringify(context, null, 2)}

${isKorean ? '검색 쿼리:' : 'Search Query:'} "${query}"

${isKorean ? '출력 형식(반드시 JSON 한 개의 오브젝트만, 그 외 텍스트 금지):' : 'Output format (MUST be a single JSON object, no extra text):'}
{
  "playlists": [
    {
      "id": string (optional),
      "title": string (required, 구체적이고 검색 가능한 제목),
      "description": string (optional, 간단한 설명),
      "channelTitle": string (optional, 채널명),
      "videoCount": number (optional, integer >= 0)
    }
  ]
}

${isKorean ? `제약 조건:
- playlists 배열 길이는 정확히 ${maxResults}개
- 제목은 구체적이고 실제 검색 가능한 형태로 작성
- 장르, 분위기, 아티스트명 등을 포함한 구체적인 제목
- 위 키 외의 키는 절대 포함하지 말 것
- 모든 텍스트는 ${isKorean ? '한국어' : 'English'}로 작성` :
`Constraints:
- playlists array length must be exactly ${maxResults}
- Titles must be specific and searchable
- Include genre, mood, artist names in specific titles
- Do NOT include any keys other than specified above
- Write all text in ${isKorean ? 'Korean' : 'English'}`}
`;

      const response = await this.aiService.makeRequest(prompt);

      if (!response.success) {
        return { success: true, playlists: [] };
      }

      const content = response.content || '';
      const parsed = this.extractFirstJSONObject(content) || {};

      const playlists = this.coercePlaylists(parsed, maxResults);
      
      // 캐시 저장 비활성화
      // if (!skipCache) {
      //   const cacheKey = this.generateCacheKey(query, maxResults, language);
      //   this.setCache(cacheKey, playlists);
      // }
      
      return { success: true, playlists, fromCache: false };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async searchPlaylists(query, maxResults = 10, language = 'ko', skipCache = false) {
    const context = { type: 'search', query };
    return await this.generateAIPlaylistRecommendations(context, maxResults, language, skipCache);
  }

  async getPopularPlaylists(maxResults = 10, language = 'ko', skipCache = false) {
    const context = { type: 'popular' };
    return await this.generateAIPlaylistRecommendations(context, maxResults, language, skipCache);
  }

  async getGenrePlaylists(genre, maxResults = 5, language = 'ko', skipCache = false) {
    const context = { type: 'genre', genre };
    return await this.generateAIPlaylistRecommendations(context, maxResults, language, skipCache);
  }

  async getMoodPlaylists(mood, maxResults = 5, language = 'ko', skipCache = false) {
    const context = { type: 'mood', mood };
    return await this.generateAIPlaylistRecommendations(context, maxResults, language, skipCache);
  }

  async getRecommendationsFromHistory(playlistHistory, maxResults = 5, language = 'ko') {
    const context = { type: 'history', history: playlistHistory };
    return await this.generateAIPlaylistRecommendations(context, maxResults, language);
  }

  async getRealTimeRecommendations(currentSongTitle, maxResults = 3, language = 'ko') {
    const context = { type: 'currentSong', songTitle: currentSongTitle };
    return await this.generateAIPlaylistRecommendations(context, maxResults, language);
  }

  // 새로운 메서드: AI 분석 결과 기반 추천
  async getRecommendationsFromAIAnalysis(aiAnalysis, maxResults = 5, language = 'ko', skipCache = false) {
    if (!aiAnalysis?.recommendations) {
      return await this.getPopularPlaylists(maxResults, language, skipCache);
    }

    const { preferredGenres, preferredMood, recommendedKeywords } = aiAnalysis.recommendations;
    
    // 사용자 취향 우선 순위로 추천 방법 결정
    const methods = [];
    
    // 1순위: 선호 장르 기반 추천 (모든 장르 시도)
    if (preferredGenres && preferredGenres.length > 0) {
      for (const genre of preferredGenres) {
        methods.push(() => this.getGenrePlaylists(genre, maxResults, language, skipCache));
      }
    }
    
    // 2순위: 선호 분위기 기반 추천 (모든 분위기 시도)
    if (preferredMood && preferredMood.length > 0) {
      for (const mood of preferredMood) {
        methods.push(() => this.getMoodPlaylists(mood, maxResults, language, skipCache));
      }
    }
    
    // 3순위: 추천 키워드 기반 추천 (모든 키워드 시도)
    if (recommendedKeywords && recommendedKeywords.length > 0) {
      for (const keyword of recommendedKeywords) {
        methods.push(() => this.searchPlaylists(keyword, maxResults, language, skipCache));
      }
    }
    
    // 4순위: 인기 플레이리스트 (마지막 보루)
    methods.push(() => this.getPopularPlaylists(maxResults, language, skipCache));
    
    // 순서대로 시도 (첫 번째 성공한 결과 반환)
    for (const method of methods) {
      try {
        const result = await method();
        if (result.success && result.playlists && result.playlists.length > 0) {
          return result;
        }
      } catch (error) {
        console.warn('추천 방법 실패, 다음 방법 시도:', error);
        continue;
      }
    }
    
    // 모든 방법이 실패하면 인기 플레이리스트 반환
    return await this.getPopularPlaylists(maxResults, language, skipCache);
  }

  // 캐시 클리어
  clearCache() {
    this.cache.clear();
  }

  // 캐시 상태 확인
  getCacheStats() {
    const now = Date.now();
    let valid = 0;
    let expired = 0;
    
    for (const [key, value] of this.cache.entries()) {
      if (now - value.timestamp < this.cacheTimeout) {
        valid++;
      } else {
        expired++;
        this.cache.delete(key);
      }
    }
    
    return { valid, expired, total: valid + expired };
  }
}

const youtubeService = new YouTubeService();
export default youtubeService;
